import { themeManager, Theme } from './themeManager';
import { Die } from '../types/game';

export type TablePresetId = 'auto' | 'casino' | 'nordic' | 'vip' | 'obsidian' | 'marble';

export interface RaytracedDieMaterial {
  /** Base RGB (0-1) of the unlocked die body */
  bodyColor: [number, number, number];
  /** Base RGB (0-1) of the locked/kept die body */
  lockedBodyColor: [number, number, number];
  /** RGB (0-1) of regular pips (2-6) */
  pipColor: [number, number, number];
  /** RGB (0-1) of the iconic #1 center pip */
  pipOneColor: [number, number, number];
  /** RGB (0-1) of pips when die is locked */
  lockedPipColor: [number, number, number];
  /** Subsurface scattering / internal glow RGB (0-1) */
  sssColor: [number, number, number];
  /** Rim / lock highlight RGB (0-1) */
  lockGlowColor: [number, number, number];
  /** Bounce light color from the table surface RGB (0-1) */
  tableBounceColor: [number, number, number];
  /** Specular power / glossiness (e.g. 64-140) */
  shininess: number;
  /** Clearcoat reflectivity (0-1) */
  reflectivity: number;
}

export interface TablePreset {
  id: TablePresetId;
  nameSv: string;
  nameEn: string;
  descSv: string;
  descEn: string;
  icon: string;
  /** Outer 3D frame (wood / metal / stone) */
  frameGradient: string;
  frameBorder: string;
  frameAccent: string;
  /** Main rolling surface (felt / velvet / leather / marble) */
  surfaceGradient: string;
  surfaceVignette: string;
  surfacePatternOpacity: number;
  /** Recessed Keep Tray (Spara-hylla) */
  trayBackground: string;
  trayBorder: string;
  slotEmptyBg: string;
  slotLockedBg: string;
  slotLockedBorder: string;
  slotLockedGlow: string;
  textColor: string;
  mutedTextColor: string;
  accentColor: string;
  dieMaterial: RaytracedDieMaterial;
}

const STORAGE_KEY = 'yatzy-table-preset';

export const TABLE_PRESETS: Record<Exclude<TablePresetId, 'auto'>, TablePreset> = {
  casino: {
    id: 'casino',
    nameSv: 'Royal Casino',
    nameEn: 'Royal Casino',
    descSv: 'Smaragdfilt, mahognysarg & elfenbenstärningar',
    descEn: 'Emerald felt, mahogany rim & ivory dice',
    icon: '🎩',
    frameGradient: 'linear-gradient(145deg, #4a1f14 0%, #2d110a 45%, #5c281a 80%, #240c06 100%)',
    frameBorder: '#8c6239',
    frameAccent: '#d4af37',
    surfaceGradient:
      'radial-gradient(ellipse at 50% 32%, rgba(46, 204, 113, 0.24) 0%, transparent 62%), ' +
      'linear-gradient(160deg, #0b4824 0%, #0f6834 48%, #093b1d 100%)',
    surfaceVignette: 'inset 0 12px 28px rgba(0,0,0,0.55), inset 0 -8px 20px rgba(0,0,0,0.45)',
    surfacePatternOpacity: 0.16,
    trayBackground: 'linear-gradient(180deg, rgba(6, 28, 14, 0.92) 0%, rgba(12, 46, 24, 0.96) 100%)',
    trayBorder: 'rgba(212, 175, 55, 0.45)',
    slotEmptyBg: 'rgba(0, 0, 0, 0.34)',
    slotLockedBg: 'radial-gradient(circle at 50% 35%, rgba(76, 175, 80, 0.32), rgba(16, 64, 32, 0.78))',
    slotLockedBorder: '#ffd54f',
    slotLockedGlow: '0 0 18px rgba(255, 213, 79, 0.45), inset 0 0 12px rgba(76, 175, 80, 0.35)',
    textColor: '#f8faf6',
    mutedTextColor: 'rgba(240, 248, 242, 0.78)',
    accentColor: '#ffd54f',
    dieMaterial: {
      bodyColor: [0.97, 0.95, 0.90],
      lockedBodyColor: [0.90, 0.98, 0.91],
      pipColor: [0.11, 0.13, 0.16],
      pipOneColor: [0.82, 0.12, 0.18],
      lockedPipColor: [0.06, 0.26, 0.12],
      sssColor: [1.0, 0.86, 0.68],
      lockGlowColor: [0.98, 0.82, 0.28],
      tableBounceColor: [0.10, 0.48, 0.24],
      shininess: 96,
      reflectivity: 0.55,
    },
  },
  nordic: {
    id: 'nordic',
    nameSv: 'Nordisk Ek',
    nameEn: 'Nordic Oak',
    descSv: 'Skandinavisk ekram, salviafilt & keramiktärningar',
    descEn: 'Scandinavian oak rim, sage wool & ceramic dice',
    icon: '🌿',
    frameGradient: 'linear-gradient(145deg, #c8a27a 0%, #a88058 50%, #d6b48e 85%, #8d6742 100%)',
    frameBorder: '#e6ccb2',
    frameAccent: '#f4e3cf',
    surfaceGradient:
      'radial-gradient(ellipse at 50% 30%, rgba(255, 255, 255, 0.16) 0%, transparent 60%), ' +
      'linear-gradient(160deg, #2d4c43 0%, #3b6257 50%, #233c35 100%)',
    surfaceVignette: 'inset 0 10px 24px rgba(0,0,0,0.42), inset 0 -6px 16px rgba(0,0,0,0.35)',
    surfacePatternOpacity: 0.12,
    trayBackground: 'linear-gradient(180deg, rgba(24, 42, 37, 0.92) 0%, rgba(32, 54, 48, 0.96) 100%)',
    trayBorder: 'rgba(230, 204, 178, 0.45)',
    slotEmptyBg: 'rgba(0, 0, 0, 0.28)',
    slotLockedBg: 'radial-gradient(circle at 50% 35%, rgba(129, 199, 132, 0.32), rgba(27, 67, 50, 0.8))',
    slotLockedBorder: '#a5d6a7',
    slotLockedGlow: '0 0 16px rgba(165, 214, 167, 0.45), inset 0 0 10px rgba(165, 214, 167, 0.25)',
    textColor: '#f5f7f6',
    mutedTextColor: 'rgba(245, 247, 246, 0.78)',
    accentColor: '#ffe082',
    dieMaterial: {
      bodyColor: [0.98, 0.98, 0.97],
      lockedBodyColor: [0.88, 0.97, 0.91],
      pipColor: [0.16, 0.22, 0.21],
      pipOneColor: [0.85, 0.33, 0.18],
      lockedPipColor: [0.08, 0.30, 0.18],
      sssColor: [0.92, 0.96, 0.95],
      lockGlowColor: [0.55, 0.88, 0.65],
      tableBounceColor: [0.22, 0.39, 0.34],
      shininess: 82,
      reflectivity: 0.48,
    },
  },
  vip: {
    id: 'vip',
    nameSv: 'Monte Carlo VIP',
    nameEn: 'Monte Carlo VIP',
    descSv: 'Vinröd sammet, mörk valnöt & champagnetärningar',
    descEn: 'Burgundy velvet, dark walnut & champagne dice',
    icon: '👑',
    frameGradient: 'linear-gradient(145deg, #361d10 0%, #1f0f07 48%, #4d2b18 82%, #170a04 100%)',
    frameBorder: '#b8860b',
    frameAccent: '#ffd700',
    surfaceGradient:
      'radial-gradient(ellipse at 50% 32%, rgba(239, 83, 80, 0.20) 0%, transparent 60%), ' +
      'linear-gradient(160deg, #4a0e1c 0%, #6b1529 52%, #380914 100%)',
    surfaceVignette: 'inset 0 12px 28px rgba(0,0,0,0.6), inset 0 -8px 20px rgba(0,0,0,0.48)',
    surfacePatternOpacity: 0.14,
    trayBackground: 'linear-gradient(180deg, rgba(36, 6, 14, 0.94) 0%, rgba(52, 10, 20, 0.97) 100%)',
    trayBorder: 'rgba(255, 215, 0, 0.48)',
    slotEmptyBg: 'rgba(0, 0, 0, 0.38)',
    slotLockedBg: 'radial-gradient(circle at 50% 35%, rgba(255, 215, 0, 0.28), rgba(74, 14, 28, 0.85))',
    slotLockedBorder: '#ffd700',
    slotLockedGlow: '0 0 18px rgba(255, 215, 0, 0.5), inset 0 0 12px rgba(255, 215, 0, 0.28)',
    textColor: '#fff8e7',
    mutedTextColor: 'rgba(255, 248, 231, 0.78)',
    accentColor: '#ffd700',
    dieMaterial: {
      bodyColor: [0.98, 0.93, 0.82],
      lockedBodyColor: [0.99, 0.96, 0.84],
      pipColor: [0.24, 0.08, 0.08],
      pipOneColor: [0.80, 0.10, 0.16],
      lockedPipColor: [0.12, 0.32, 0.14],
      sssColor: [1.0, 0.78, 0.42],
      lockGlowColor: [1.0, 0.84, 0.22],
      tableBounceColor: [0.42, 0.09, 0.16],
      shininess: 112,
      reflectivity: 0.64,
    },
  },
  obsidian: {
    id: 'obsidian',
    nameSv: 'Cyber Obsidian',
    nameEn: 'Cyber Obsidian',
    descSv: 'Kolfiber, neonljus & obsidiantärningar',
    descEn: 'Carbon fiber, neon glow & obsidian dice',
    icon: '⚡',
    frameGradient: 'linear-gradient(145deg, #1a1d2e 0%, #0d0f1a 50%, #252942 85%, #090a12 100%)',
    frameBorder: '#00e5ff',
    frameAccent: '#00ff88',
    surfaceGradient:
      'radial-gradient(ellipse at 50% 32%, rgba(0, 229, 255, 0.18) 0%, transparent 62%), ' +
      'linear-gradient(160deg, #0d1224 0%, #141c36 52%, #090c18 100%)',
    surfaceVignette: 'inset 0 12px 28px rgba(0,0,0,0.7), inset 0 0 24px rgba(0, 229, 255, 0.12)',
    surfacePatternOpacity: 0.20,
    trayBackground: 'linear-gradient(180deg, rgba(7, 10, 22, 0.95) 0%, rgba(13, 19, 38, 0.98) 100%)',
    trayBorder: 'rgba(0, 255, 136, 0.5)',
    slotEmptyBg: 'rgba(0, 0, 0, 0.48)',
    slotLockedBg: 'radial-gradient(circle at 50% 35%, rgba(0, 255, 136, 0.28), rgba(10, 28, 36, 0.88))',
    slotLockedBorder: '#00ff88',
    slotLockedGlow: '0 0 20px rgba(0, 255, 136, 0.55), inset 0 0 12px rgba(0, 229, 255, 0.35)',
    textColor: '#e8faff',
    mutedTextColor: 'rgba(232, 250, 255, 0.76)',
    accentColor: '#00ff88',
    dieMaterial: {
      bodyColor: [0.16, 0.19, 0.28],
      lockedBodyColor: [0.12, 0.28, 0.24],
      pipColor: [0.0, 0.92, 1.0],
      pipOneColor: [1.0, 0.18, 0.72],
      lockedPipColor: [0.15, 1.0, 0.58],
      sssColor: [0.0, 0.75, 0.95],
      lockGlowColor: [0.0, 1.0, 0.55],
      tableBounceColor: [0.05, 0.35, 0.48],
      shininess: 128,
      reflectivity: 0.72,
    },
  },
  marble: {
    id: 'marble',
    nameSv: 'Kejserlig Marmor',
    nameEn: 'Imperial Marble',
    descSv: 'Mörk marmor, borstad brons & kristalltärningar',
    descEn: 'Dark marble, brushed bronze & crystal dice',
    icon: '🏛️',
    frameGradient: 'linear-gradient(145deg, #3c352e 0%, #211d19 48%, #52483e 82%, #181512 100%)',
    frameBorder: '#c5a059',
    frameAccent: '#e6c687',
    surfaceGradient:
      'radial-gradient(ellipse at 45% 30%, rgba(197, 160, 89, 0.16) 0%, transparent 60%), ' +
      'linear-gradient(155deg, #1b1f24 0%, #282e36 48%, #14171b 100%)',
    surfaceVignette: 'inset 0 12px 28px rgba(0,0,0,0.62), inset 0 -8px 20px rgba(0,0,0,0.48)',
    surfacePatternOpacity: 0.15,
    trayBackground: 'linear-gradient(180deg, rgba(16, 18, 22, 0.94) 0%, rgba(24, 28, 34, 0.97) 100%)',
    trayBorder: 'rgba(197, 160, 89, 0.48)',
    slotEmptyBg: 'rgba(0, 0, 0, 0.42)',
    slotLockedBg: 'radial-gradient(circle at 50% 35%, rgba(197, 160, 89, 0.30), rgba(26, 42, 34, 0.85))',
    slotLockedBorder: '#e6c687',
    slotLockedGlow: '0 0 18px rgba(230, 198, 135, 0.48), inset 0 0 10px rgba(197, 160, 89, 0.28)',
    textColor: '#f5f2eb',
    mutedTextColor: 'rgba(245, 242, 235, 0.76)',
    accentColor: '#e6c687',
    dieMaterial: {
      bodyColor: [0.95, 0.96, 0.99],
      lockedBodyColor: [0.89, 0.97, 0.92],
      pipColor: [0.12, 0.16, 0.26],
      pipOneColor: [0.78, 0.56, 0.16],
      lockedPipColor: [0.08, 0.30, 0.16],
      sssColor: [0.82, 0.88, 1.0],
      lockGlowColor: [0.90, 0.78, 0.48],
      tableBounceColor: [0.22, 0.24, 0.28],
      shininess: 118,
      reflectivity: 0.68,
    },
  },
};

/** Maps a theme id to the best matching 3D table preset when 'auto' is selected. */
export function resolveTablePreset(presetId: TablePresetId, theme?: Theme): TablePreset {
  if (presetId !== 'auto' && TABLE_PRESETS[presetId]) {
    return TABLE_PRESETS[presetId];
  }
  const activeTheme = theme || themeManager.getCurrentTheme();
  switch (activeTheme.id) {
    case 'neon':
      return TABLE_PRESETS.obsidian;
    case 'vintage':
      return TABLE_PRESETS.vip;
    case 'minimalist':
      return TABLE_PRESETS.nordic;
    case 'ocean':
      return TABLE_PRESETS.marble;
    case 'sunset':
      return TABLE_PRESETS.vip;
    case 'default':
    default:
      return TABLE_PRESETS.casino;
  }
}

export function getSavedTablePresetId(): TablePresetId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) as TablePresetId | null;
    if (raw && (raw === 'auto' || raw in TABLE_PRESETS)) {
      return raw;
    }
  } catch {}
  return 'auto';
}

const listeners = new Set<(id: TablePresetId) => void>();

export function setSavedTablePresetId(id: TablePresetId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {}
  listeners.forEach(fn => fn(id));
}

export function subscribeTablePreset(fn: (id: TablePresetId) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export interface QuickKeepOption {
  id: string;
  labelSv: string;
  labelEn: string;
  /** Which dice IDs should be locked when this quick action is clicked */
  targetLockedIds: number[];
  badge?: string;
}

/**
 * Analyserar aktuella tärningsvärden och tar fram smarta 1-klicksval för att
 * spara par, triss, fyrtal, kåk, stege eller högsta valör.
 */
export function computeQuickKeepOptions(dice: Die[]): QuickKeepOption[] {
  const validDice = dice.filter(d => d.value >= 1 && d.value <= 6);
  if (validDice.length === 0) return [];

  const options: QuickKeepOption[] = [];
  const byValue = new Map<number, number[]>();
  for (const d of validDice) {
    const list = byValue.get(d.value) || [];
    list.push(d.id);
    byValue.set(d.value, list);
  }

  // 1. Grupper med minst 2 lika (sortera efter antal fallande, sedan valör fallande)
  const groups = Array.from(byValue.entries())
    .filter(([, ids]) => ids.length >= 2)
    .sort((a, b) => b[1].length - a[1].length || b[0] - a[0]);

  for (const [val, ids] of groups) {
    const count = ids.length;
    options.push({
      id: `val-${val}`,
      labelSv: `Spara ${val}:or (${count} st)`,
      labelEn: `Keep ${val}s (${count})`,
      targetLockedIds: ids,
      badge: `${val}×${count}`,
    });
  }

  // 2. Om det finns två par eller kåk (minst 2 grupper med >= 2 tärningar), erbjud att spara båda
  if (groups.length >= 2) {
    const combinedIds = [...groups[0][1], ...groups[1][1]];
    const isFullHouse = groups[0][1].length >= 3 && groups[1][1].length >= 2;
    options.push({
      id: 'combo-pairs',
      labelSv: isFullHouse ? 'Spara kåk' : `Spara båda paren (${groups[0][0]} & ${groups[1][0]})`,
      labelEn: isFullHouse ? 'Keep full house' : `Keep both pairs (${groups[0][0]} & ${groups[1][0]})`,
      targetLockedIds: combinedIds,
    });
  }

  // 3. Stege-detektion: om minst 4 unika tärningar ligger i sekvens (1-2-3-4, 2-3-4-5, 3-4-5-6)
  const straightSequences = [
    [1, 2, 3, 4, 5, 6],
    [1, 2, 3, 4, 5],
    [2, 3, 4, 5, 6],
    [1, 2, 3, 4],
    [2, 3, 4, 5],
    [3, 4, 5, 6],
  ];
  for (const seq of straightSequences) {
    if (seq.every(v => byValue.has(v))) {
      const straightIds = seq.map(v => byValue.get(v)![0]);
      options.push({
        id: `straight-${seq.join('')}`,
        labelSv: `Spara stege (${seq[0]}–${seq[seq.length - 1]})`,
        labelEn: `Keep straight (${seq[0]}–${seq[seq.length - 1]})`,
        targetLockedIds: straightIds,
      });
      break;
    }
  }

  // 4. Om inga par finns alls, erbjud att spara högsta tärningen (t.ex. 6:an eller 5:an)
  if (groups.length === 0) {
    const highestVal = Math.max(...validDice.map(d => d.value));
    const highestIds = byValue.get(highestVal) || [];
    if (highestIds.length > 0) {
      options.push({
        id: `high-${highestVal}`,
        labelSv: `Spara högsta (${highestVal})`,
        labelEn: `Keep highest (${highestVal})`,
        targetLockedIds: highestIds,
      });
    }
  }

  return options.slice(0, 4);
}

/**
 * Beskriver kortfattat mönstret bland de sparade (låsta) tärningarna.
 */
export function describeKeptPattern(dice: Die[], locale: 'sv' | 'en' = 'sv'): string {
  const locked = dice.filter(d => d.isLocked && d.value >= 1 && d.value <= 6);
  if (locked.length === 0) {
    return locale === 'en'
      ? 'Click dice on the table (or press 1–6) to move them into the keep tray'
      : 'Klicka på tärningarna (eller tryck 1–6) för att lägga dem i spara-hyllan';
  }

  const counts = new Map<number, number>();
  let sum = 0;
  for (const d of locked) {
    counts.set(d.value, (counts.get(d.value) || 0) + 1);
    sum += d.value;
  }

  const sortedCounts = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const [topVal, topCount] = sortedCounts[0];

  let patternSv = '';
  let patternEn = '';

  if (topCount >= 5) {
    patternSv = `Yatzy-chans (${topCount}×${topVal})`;
    patternEn = `Yatzy锁定 (${topCount}×${topVal})`;
    patternEn = `Yatzy set (${topCount}×${topVal})`;
  } else if (topCount === 4) {
    patternSv = `Fyrtal i ${topVal}:or`;
    patternEn = `Four ${topVal}s`;
  } else if (topCount === 3 && sortedCounts[1]?.[1] >= 2) {
    patternSv = `Kåk (${topVal}:or & ${sortedCounts[1][0]}:or)`;
    patternEn = `Full house (${topVal}s & ${sortedCounts[1][0]}s)`;
  } else if (topCount === 3) {
    patternSv = `Triss i ${topVal}:or`;
    patternEn = `Three ${topVal}s`;
  } else if (topCount === 2 && sortedCounts[1]?.[1] === 2) {
    patternSv = `Två par (${topVal}:or & ${sortedCounts[1][0]}:or)`;
    patternEn = `Two pairs (${topVal}s & ${sortedCounts[1][0]}s)`;
  } else if (topCount === 2) {
    patternSv = `Par i ${topVal}:or`;
    patternEn = `Pair of ${topVal}s`;
  } else {
    const vals = locked.map(d => d.value).sort((a, b) => a - b);
    const isSeq = vals.length >= 3 && vals.every((v, i) => i === 0 || v === vals[i - 1] + 1);
    if (isSeq) {
      patternSv = `Stege på gång (${vals.join('–')})`;
      patternEn = `Straight draw (${vals.join('–')})`;
    } else {
      patternSv = `Valörer: ${vals.join(', ')}`;
      patternEn = `Values: ${vals.join(', ')}`;
    }
  }

  return locale === 'en'
    ? `${patternEn} • Sum: ${sum}`
    : `${patternSv} • Summa: ${sum}`;
}
